import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Show, useUser } from "@clerk/react";
import { Package } from "lucide-react";
import { useApi, euro, imgSrc, ORDER_STATUS, statusLabel } from "@/lib/api";
import { MyAlerts } from "@/components/MyAlerts";
import { OrderReturn } from "@/components/OrderReturn";

export const StatusBadge = ({ status, order }) => {
  const s = ORDER_STATUS[status] || ORDER_STATUS.pagado;
  return <span className={`tag ${s.cls}`}>{statusLabel(status, order)}</span>;
};

const Orders = () => {
  const call = useApi();
  const { user } = useUser();
  const [orders, setOrders] = useState(null);
  const load = useCallback(() => call("get", "/orders/mine").then((r) => setOrders(r.data)).catch(() => setOrders([])), [call]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-14" data-testid="account-page">
      <p className="eyebrow">Mi cuenta</p>
      <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight">Hola, {user?.firstName || "deportista"}</h1>
      <h2 className="mt-12 filter-title">Mis pedidos</h2>
      {orders === null && <p className="text-slate-500">Cargando…</p>}
      {orders?.length === 0 && (
        <div className="border border-dashed p-12 text-center text-slate-500" data-testid="orders-empty">
          <Package className="w-8 h-8 mx-auto mb-3" /> Aún no tienes pedidos.
        </div>
      )}
      <div className="space-y-4">
        {orders?.map((o) => (
          <div key={o.id} className="border bg-white p-6" data-testid={`order-${o.number}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono text-sm font-bold">{o.number}</p>
                <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString("es-ES", { dateStyle: "long" })}</p>
              </div>
              <StatusBadge status={o.status} order={o} />
              <p className="font-bold">{euro(o.total)}</p>
            </div>
            <div className="mt-4 flex gap-3 overflow-x-auto">
              {o.items.map((i, k) => (
                <div key={k} className="flex items-center gap-2 text-xs shrink-0">
                  <img src={imgSrc(i.image)} alt="" className="w-10 h-12 object-cover bg-slate-100" />
                  <span>{i.name}<br /><span className="text-slate-500">T. {i.size}{i.color && ` · ${i.color}`} · x{i.quantity}</span></span>
                </div>
              ))}
            </div>
            <OrderReturn order={o} onDone={load} />
            {o.status === "pendiente_pago" && <Link to={`/pedido/${o.id}`} className="mt-4 inline-block text-sm font-semibold underline text-brand" data-testid={`order-howto-pay-${o.number}`}>Ver cómo pagar</Link>}
          </div>
        ))}
      </div>
      <MyAlerts />
    </div>
  );
};

export default function Account() {
  return (
    <>
      <Show when="signed-in"><Orders /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
